import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "time-selector-wrapper" }
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "time-selector__required"
}
const _hoisted_4 = ["id"]
const _hoisted_5 = {
  key: 1,
  class: "time-selector__helper"
}
const _hoisted_6 = { class: "time-selector__error" }
const _hoisted_7 = { class: "time-selector__header" }
const _hoisted_8 = {
  key: 0,
  class: "time-selector__loading-shimmer"
}
const _hoisted_9 = {
  key: 0,
  class: "time-selector__header-item"
}
const _hoisted_10 = ["data-value", "onClick"]
const _hoisted_11 = { class: "time-selector__item-time" }
const _hoisted_12 = {
  key: 0,
  class: "time-selector__item-busy-indicator"
}

import { Icon } from "@/components/ui";
import { computed, onMounted, onUnmounted, ref, nextTick } from "vue";

type SlotItem =
  | { type: "time"; value: string; display: string }
  | { type: "header"; label: string };

interface Props {
  modelValue?: string;
  interval?: number;
  startHour?: number;
  label?: string;
  required?: boolean;
  id?: string;
  error?: string;
  busySlots?: string[];
  loadingSlots?: boolean;
  timezone?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TimeSelector',
  props: {
    modelValue: { default: "" },
    interval: { default: 15 },
    startHour: { default: 0 },
    label: {},
    required: { type: Boolean, default: false },
    id: {},
    error: { default: "" },
    busySlots: { default: () => [] },
    loadingSlots: { type: Boolean, default: false },
    timezone: { default: "" }
  },
  emits: ["update:modelValue", "blur"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
const listRef = ref<HTMLElement | null>(null);
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

const timeSelectorId = computed(
  () => props.id || `time-selector-${Math.random().toString(36).substr(2, 9)}`
);

const isBusy = (timeValue: string): boolean => {
  return props.busySlots?.includes(timeValue) || false;
};

const allTimes = computed((): SlotItem[] => {
  const items: SlotItem[] = [];
  const sections = [
    { label: "Morning", startHour: props.startHour, endHour: 12 },
    { label: "Afternoon", startHour: 12, endHour: 18 },
    { label: "Evening", startHour: 18, endHour: 24 },
  ];

  for (const section of sections) {
    let headerAdded = false;

    for (let h = section.startHour; h < section.endHour && h < 24; h++) {
      for (let m = 0; m < 60; m += props.interval) {
        // Skip if hour is before startHour
        if (h < props.startHour) continue;

        // Add section header before first slot in section
        if (!headerAdded && (h !== props.startHour || m === 0)) {
          items.push({ type: "header", label: section.label });
          headerAdded = true;
        }

        const hours24 = String(h).padStart(2, "0");
        const minutes = String(m).padStart(2, "0");
        const value24 = `${hours24}:${minutes}`;

        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h < 12 ? "AM" : "PM";
        const display = `${String(hour12).padStart(2, "0")}:${minutes} ${ampm}`;

        items.push({
          type: "time",
          value: value24,
          display,
        });
      }
    }
  }

  return items;
});

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const [hours, minutes] = props.modelValue.split(":");
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);

  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
});

const selectTime = (value: string) => {
  emit("update:modelValue", value);
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const scrollToSelected = async () => {
  await nextTick();
  if (!listRef.value) return;

  let targetElement: HTMLElement | null = null;

  // Find selected element, or nearest future slot
  if (props.modelValue) {
    targetElement = listRef.value.querySelector(
      `.time-selector__item[data-value="${props.modelValue}"]`
    );
  }

  // If no selection, find nearest future slot (in the selected timezone if provided)
  if (!targetElement) {
    const now = new Date();
    let currentTime: string;

    if (props.timezone) {
      // Format current time in the selected timezone to match busySlots format
      const formatter = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: props.timezone,
      });
      currentTime = formatter.format(now);
    } else {
      // Fallback to local timezone if not provided
      const currentHour = String(now.getHours()).padStart(2, "0");
      const currentMin = String(now.getMinutes()).padStart(2, "0");
      currentTime = `${currentHour}:${currentMin}`;
    }

    const allItems = listRef.value.querySelectorAll(".time-selector__item");
    for (const item of allItems) {
      const value = item.getAttribute("data-value");
      if (value && value >= currentTime) {
        targetElement = item as HTMLElement;
        break;
      }
    }
  }

  // Default to first time slot if none found
  if (!targetElement) {
    targetElement = listRef.value.querySelector(".time-selector__item");
  }

  if (targetElement) {
    targetElement.scrollIntoView({ block: "center", behavior: "instant" });
  }
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 250;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10070",
  };
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    setTimeout(() => {
      updateDropdownPosition();
      scrollToSelected();
    }, 0);
  }
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".time-selector-wrapper") &&
    !relatedTarget.closest(".time-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

onMounted(() => {
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target && !target.closest(".time-selector-wrapper") && !target.closest(".time-selector")) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: timeSelectorId.value,
          class: "time-selector__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      id: timeSelectorId.value,
      ref_key: "triggerRef",
      ref: triggerRef,
      class: _normalizeClass(["time-selector__trigger", { 'time-selector__trigger--open': isOpen.value, 'time-selector__trigger--error': !!__props.error }]),
      onClick: toggleDropdown,
      onBlur: handleBlur,
      tabindex: "0"
    }, [
      _createElementVNode("span", {
        class: _normalizeClass(["time-selector__trigger-text", { 'is-placeholder': !displayValue.value }])
      }, _toDisplayString(displayValue.value || "Select Time"), 3),
      _createVNode(_unref(Icon), {
        icon: "mdi:chevron-down",
        class: "time-selector__trigger-icon"
      })
    ], 42, _hoisted_4),
    (__props.error)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("span", _hoisted_6, _toDisplayString(__props.error), 1)
        ]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "time-selector",
            style: _normalizeStyle(dropdownStyle.value)
          }, [
            _createElementVNode("div", _hoisted_7, [
              _cache[0] || (_cache[0] = _createElementVNode("span", { class: "time-selector__header-label" }, "TIME", -1)),
              (__props.loadingSlots)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", {
              class: "time-selector__list",
              ref_key: "listRef",
              ref: listRef
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allTimes.value, (item) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: item.type === 'time' ? item.value : item.label
                }, [
                  (item.type === 'header')
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(item.label), 1))
                    : (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        "data-value": item.value,
                        class: _normalizeClass(["time-selector__item", {
                'time-selector__item--selected': __props.modelValue === item.value,
                'time-selector__item--busy': isBusy(item.value),
              }]),
                        onClick: _withModifiers(($event: any) => (selectTime(item.value)), ["stop"])
                      }, [
                        _createElementVNode("span", _hoisted_11, _toDisplayString(item.display), 1),
                        (isBusy(item.value))
                          ? (_openBlock(), _createElementBlock("span", _hoisted_12, "●"))
                          : _createCommentVNode("", true)
                      ], 10, _hoisted_10))
                ], 64))
              }), 128))
            ], 512)
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})